import { Dialog, DialogContent, DialogTitle, Menu, MenuItem } from '@mui/material'
import { AuthorizedFeature } from 'components'
import { EditCoachProfileDialog } from 'components/CoachProfile/EditCoachProfileDialog'
import { useTimeTracker } from 'components/TimeTracker/TimeTrackerProvider'
import { useCanEditCoachProfile } from 'components/UserProfile/hooks/useCanEditCoachProfile'
import { useAuth } from 'context'
import { useModal } from 'hooks'
import { LocationPaths } from 'location.types'
import { useNavigate } from 'react-router'
import { EhrCoachRegistrationStatus, FeatureGrant, useCurrentUserQuery } from 'types'
import { EditAddressDialog } from './EditAddressDialog'
import { EditEmailDialog } from './EditEmailDialog'
import { EditProfileDialog } from './EditProfileDialog'
import { ManageUiStatesDialog } from './ManageUiStatesDialog'
import { OrganizationDialog } from './OrganizationDialog'
import { Profile } from './Profile'
import { UpdateSupportedSensorDialog } from './UpdateSupportedSensorDialog'

interface IUserMenu {
  anchorEl: HTMLElement | null
  state: boolean
  close: () => void
}

export const UserMenu = ({ anchorEl, state, close }: IUserMenu) => {
  const {
    modalState: profileModalState,
    openModal: openProfileModal,
    closeModal: closeProfileModal
  } = useModal()
  const {
    modalState: organizationModalState,
    openModal: openOrganizationModal,
    closeModal: closeOrganizationModal
  } = useModal()
  const {
    modalState: editProfileModalState,
    openModal: openEditProfileModal,
    closeModal: closeEditProfileModal
  } = useModal()
  const {
    modalState: editAddressModalState,
    openModal: openEditAddressModal,
    closeModal: closeEditAddressModal
  } = useModal()
  const {
    modalState: editEmailModalState,
    openModal: openEditEmailModal,
    closeModal: closeEditEmailModal
  } = useModal()
  const {
    modalState: editCoachProfileModalState,
    openModal: editCoachProfileModal,
    closeModal: closeCoachProfileModal
  } = useModal()
  const {
    modalState: supportedSensorModalState,
    openModal: openSupportedSensorModal,
    closeModal: closeSupportedSensorModal
  } = useModal()
  const {
    modalState: manageUiStatesModalState,
    openModal: openManageUiStatesModal,
    closeModal: closeManageUiStatesModal
  } = useModal()

  const { logout } = useAuth()
  const { endTrackingSession } = useTimeTracker()
  const canEditCoachProfile = useCanEditCoachProfile()

  const navigate = useNavigate()
  const { data: { currentUser } = {} } = useCurrentUserQuery()
  // The logged-in coach manages their own credential/licences from here once approved.
  const isApprovedCoach =
    currentUser?.coachProfile?.registrationStatus === EhrCoachRegistrationStatus.Approved

  return (
    <>
      <Menu open={state} anchorEl={anchorEl} onClose={close} onClick={close}>
        <MenuItem onClick={openProfileModal}>Member Profile</MenuItem>
        <AuthorizedFeature feature={FeatureGrant.UsersEditProfile}>
          <MenuItem onClick={openEditProfileModal}>Edit Member Profile</MenuItem>
        </AuthorizedFeature>
        <AuthorizedFeature feature={FeatureGrant.UsersEditAddress}>
          <MenuItem onClick={openEditAddressModal}>Edit Member Address</MenuItem>
        </AuthorizedFeature>
        <AuthorizedFeature feature={FeatureGrant.UsersChangeEmail}>
          <MenuItem onClick={openEditEmailModal}>Change Member Email</MenuItem>
        </AuthorizedFeature>
        <AuthorizedFeature feature={FeatureGrant.UsersManageUiStates}>
          <MenuItem onClick={openManageUiStatesModal}>Manage UI States</MenuItem>
        </AuthorizedFeature>
        <AuthorizedFeature feature={FeatureGrant.SupportedSensorManagement}>
          <MenuItem onClick={openSupportedSensorModal}>Update Supported Sensor</MenuItem>
        </AuthorizedFeature>
        <AuthorizedFeature feature={FeatureGrant.UsersEditOrganization}>
          <MenuItem onClick={openOrganizationModal}>Member Organization</MenuItem>
        </AuthorizedFeature>
        {canEditCoachProfile && (
          <MenuItem onClick={editCoachProfileModal}>Edit Coach Profile</MenuItem>
        )}
        {isApprovedCoach && (
          <MenuItem onClick={() => navigate(LocationPaths.CoachCredentials)}>
            Credentials &amp; Licenses
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            endTrackingSession(false)
            logout()
          }}
        >
          Sign out
        </MenuItem>
      </Menu>

      <Dialog open={profileModalState} onClose={closeProfileModal}>
        <DialogTitle>Member Profile</DialogTitle>
        <DialogContent>
          <Profile />
        </DialogContent>
      </Dialog>

      {editProfileModalState && (
        <EditProfileDialog closeModal={closeEditProfileModal} modalState={editProfileModalState} />
      )}

      {editAddressModalState && (
        <EditAddressDialog closeModal={closeEditAddressModal} modalState={editAddressModalState} />
      )}

      {editEmailModalState && (
        <EditEmailDialog closeModal={closeEditEmailModal} modalState={editEmailModalState} />
      )}

      {supportedSensorModalState && (
        <UpdateSupportedSensorDialog
          closeModal={closeSupportedSensorModal}
          modalState={supportedSensorModalState}
        />
      )}

      {organizationModalState && (
        <OrganizationDialog
          closeModal={closeOrganizationModal}
          modalState={organizationModalState}
        />
      )}

      {editCoachProfileModalState && (
        <EditCoachProfileDialog
          closeModal={closeCoachProfileModal}
          modalState={editCoachProfileModalState}
        />
      )}

      {manageUiStatesModalState && (
        <ManageUiStatesDialog
          closeModal={closeManageUiStatesModal}
          modalState={manageUiStatesModalState}
        />
      )}
    </>
  )
}
